'use client';

import { useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingsIcon,
  ChatTextIcon,
  CheckCircleIcon,
  CheckIcon,
  HomeIcon,
  LeafIcon,
  MapPinIcon,
  PhoneCallIcon,
} from '@/components/icons';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/inputs/input';
import {
  useCheckoutStore,
  selectContact,
  selectDeliveryDetails,
  selectPreferences,
} from '@/stores/checkout-store';
import { useAuthStore, selectUser } from '@/stores/auth-store';
import { useAddressStore, selectSelectedAddress } from '@/stores/address-store';
import { useCartStore, selectCartTotal } from '@/stores/cart-store';
import { useMoneyRules, isFreeDeliveryPromoActive } from '@/lib/money-rules';
import { deliveryTimeLabel } from '@/components/delivery/delivery-model';
import { SavedAddressPicker } from '@/components/features/address/saved-address-picker';

// ========== TYPES ==========

interface DeliveryDetailsStepProps {
  onNext?: () => void;
  onBack?: () => void;
}


// ========== TOGGLE BUTTON (original) ==========

function ToggleOption({
  active,
  icon: Icon,
  title,
  description,
  onClick,
  isDark,
}: {
  active: boolean;
  icon: React.ElementType;
  title: string;
  description: string;
  onClick: () => void;
  isDark: boolean;
}) {
  return (
    <motion.button
      onClick={onClick}
      className={cn(
        'w-full p-4 rounded-card border transition-all text-left',
        active
          ? isDark
            ? 'border-[var(--primary)] bg-[var(--primary-subtle)]'
            : 'border-[var(--primary)] bg-[var(--violet-50)]'
          : isDark
            ? 'border-[var(--border-default)] bg-[var(--surface-secondary)] hover:border-[var(--primary)]/50'
            : 'border-[var(--border-default)] bg-[var(--surface-primary)] hover:border-[var(--primary)]/50'
      )}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-10 h-10 rounded-pill flex items-center justify-center flex-shrink-0',
          active
            ? 'bg-[var(--primary)] text-[var(--text-inverse)]'
            : isDark
              ? 'bg-[var(--surface-tertiary)] text-[var(--text-secondary)]'
              : 'bg-[var(--surface-secondary)] text-[var(--text-secondary)]'
        )}>
          <Icon weight={active ? 'fill' : 'regular'} className="w-5 h-5" />
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className={cn(
              'font-semibold text-sm',
              // Обе ветки давали один и тот же #7B42FF: --primary равен ему
              // в светлой и в тёмной теме. Развилка была фиктивной.
              active ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]'
            )}>
              {title}
            </span>

            <div className={cn(
              'w-6 h-6 rounded-pill border-2 flex items-center justify-center transition-all',
              active
                ? 'border-[var(--primary)] bg-[var(--primary)]'
                : 'border-[var(--border-default)]'
            )}>
              {active && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <CheckIcon weight="bold" className="w-3 h-3 text-[var(--text-inverse)]" />
                </motion.div>
              )}
            </div>
          </div>

          <p className="text-xs mt-1 text-[var(--text-secondary)]">
            {description}
          </p>
        </div>
      </div>
    </motion.button>
  );
}


// ========== INLINE DELIVERY INFO (mobile) ==========

function InlineDeliveryInfo() {
  const selectedAddress = useAddressStore(selectSelectedAddress);
  const cartTotal = useCartStore(selectCartTotal);
  // Хук обязан вызываться до любого раннего return, поэтому правила берём здесь.
  // Пороги приходят с сервера (админка «Деньги») — своей копии чисел тут нет.
  const moneyRules = useMoneyRules();
  if (!selectedAddress?.isInZone) return null;
  const rawCost = selectedAddress.deliveryCost ?? 0;
  const isFreePromo = isFreeDeliveryPromoActive(rawCost, cartTotal, moneyRules);
  const cost = (rawCost === 0 || isFreePromo) ? 'Бесплатно' : `${rawCost} ₽`;
  const time = selectedAddress.estimatedTime
    ? deliveryTimeLabel(selectedAddress.estimatedTime)
    : '~60 мин';
  const min = `от ${selectedAddress.minOrderSum || 0} ₽`;

  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="mt-3">
      <div className="flex items-center rounded-card overflow-hidden border border-[var(--primary)]/20">
        <div className="w-1 self-stretch bg-[var(--primary)]" />
        <div className="flex-1 flex items-center justify-between py-2.5 pr-3 pl-2">
          <div className="flex items-center gap-1.5">
            <CheckCircleIcon className="h-4 w-4 text-[var(--primary)]" weight="fill" />
            <span className="text-xs font-semibold text-[var(--text-primary)]">Доставим</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[var(--primary)]">{cost}</span>
            <span className="text-[10px] text-[var(--text-quaternary)]">•</span>
            <span className="text-xs text-[var(--text-secondary)]">{time}</span>
            <span className="text-[10px] text-[var(--text-quaternary)]">•</span>
            <span className="text-xs text-[var(--text-secondary)]">{min}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ========== INLINE ADDRESS PICKER (mobile only) ==========

/**
 * Адрес на шаге доставки — только для чтения.
 *
 * Текст адреса намеренно не кликабелен: единственный способ его сменить —
 * кнопка справа, открывающая выбор адреса с картой. Так исключается случайное
 * стирание уже подтверждённого адреса прямо перед оплатой.
 *
 * Источник истины — selectedAddress из address-store, а не deliveryDetails.address
 * из checkout-store: второй персистится отдельно и может пережить сброс адреса,
 * показав то, чего уже нет.
 */
function AddressField({ isDark }: { isDark: boolean }) {
  const selectedAddress = useAddressStore(selectSelectedAddress);
  const openAddressSheet = useAddressStore((s) => s.openSheet);
  const errors = useCheckoutStore((s) => s.errors);

  const handleChange = useCallback(() => {
    openAddressSheet('checkout');
  }, [openAddressSheet]);

  const isInZone = selectedAddress?.isInZone === true;
  const isOutOfZone = selectedAddress != null && !selectedAddress.isInZone;
  const hasError = Boolean(errors.address) || isOutOfZone;

  return (
    <div>
      <label className="block mb-1.5 text-sm font-medium text-[var(--text-secondary)]">
        Адрес доставки *
      </label>

      <div
        className={cn(
          'flex items-center gap-3 min-h-14 px-3 py-2.5 rounded-card border transition-colors',
          hasError
            ? 'border-[var(--error)] bg-[var(--error)]/5'
            : isInZone
              ? 'border-[var(--primary)]/40 bg-[var(--primary)]/5'
              : cn('border-[var(--border-default)]', isDark ? 'bg-[var(--surface-tertiary)]' : 'bg-[var(--surface-secondary)]')
        )}
      >
        {isInZone ? (
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-[var(--primary)]" weight="fill" />
        ) : (
          <MapPinIcon
            className={cn('h-5 w-5 flex-shrink-0', hasError ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]')}
            weight="fill"
          />
        )}

        {/* Не input: нечего фокусировать, нечего править, нечего очистить.
            line-clamp-2, а не truncate — при обрезке в одну строку первым
            отваливается номер дома, а его как раз и проверяют. */}
        <p
          className={cn(
            'flex-1 min-w-0 text-sm font-medium line-clamp-2 break-words',
            selectedAddress ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'
          )}
        >
          {selectedAddress?.address || 'Адрес не указан'}
        </p>

        <button
          type="button"
          onClick={handleChange}
          className={cn(
            'flex-shrink-0 h-9 px-3 rounded-card text-xs font-semibold transition-colors active:scale-[0.98]',
            selectedAddress
              ? isDark
                ? 'bg-[var(--surface-secondary)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]'
                : 'bg-white text-[var(--primary)] hover:bg-[var(--surface-secondary)]'
              : 'bg-[var(--primary)] text-white hover:bg-[var(--primary)]/90'
          )}
        >
          {selectedAddress ? 'Сменить адрес' : 'Указать адрес'}
        </button>
      </div>

      {isOutOfZone && (
        <p className="mt-1.5 text-xs font-medium text-[var(--error)]">
          Не доставляем по этому адресу.{' '}
          <a href="/delivery" className="underline underline-offset-2">Посмотреть зоны</a>
        </p>
      )}
      {!selectedAddress && Boolean(errors.address) && (
        <p className="mt-1.5 text-xs font-medium text-[var(--error)]">Укажите адрес доставки</p>
      )}

      {isInZone && <InlineDeliveryInfo />}
    </div>
  );
}



// ========== MAIN COMPONENT ==========

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function DeliveryDetailsStep(_props: DeliveryDetailsStepProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';


  // Auth Store
  const user = useAuthStore(selectUser);

  // Address Store
  const selectedAddress = useAddressStore(selectSelectedAddress);
  const openAddressSheet = useAddressStore((state) => state.openSheet);

  // Checkout Store
  const contact = useCheckoutStore(selectContact);
  const deliveryDetails = useCheckoutStore(selectDeliveryDetails);
  const preferences = useCheckoutStore(selectPreferences);
  const setContact = useCheckoutStore((state) => state.setContact);
  const setDeliveryDetails = useCheckoutStore((state) => state.setDeliveryDetails);
  const setPreferences = useCheckoutStore((state) => state.setPreferences);
  const errors = useCheckoutStore((state) => state.errors);

  // Pre-fill from user/address data
  useEffect(() => {
    if (user?.name && !contact.name) {
      setContact({ name: user.name });
    }
    if (user?.phone && !contact.phone) {
      setContact({ phone: user.phone });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Address is synced to checkout in cart-sheet.tsx (global sync effect)
  // No need to duplicate here

  // Handlers
  return (
    <div className="p-4">
      {/* Contact Info */}
      <div className="mb-4">
        <Input
          id="name"
          label="Имя *"
          value={contact.name}
          onChange={(e) => setContact({ name: e.target.value })}
          error={errors.name ? 'Пожалуйста, введите ваше имя' : undefined}
        />
      </div>

      <div className="mb-4">
        <Input
          id="phone"
          label="Телефон *"
          value={contact.phone}
          onChange={(e) => setContact({ phone: e.target.value })}
          type="tel"
          error={errors.phone ? 'Пожалуйста, введите номер телефона' : undefined}
        />
      </div>

      {/* Сохранённые адреса: одно нажатие подставляет адрес вместе с квартирой,
          подъездом, этажом и домофоном — вводить их повторно не нужно. */}
      <SavedAddressPicker />

      {/* Адрес — только чтение. Менять можно единственным способом: кнопкой,
          которая открывает выбор адреса с картой. Раньше здесь было два разных
          интерфейса: на мобильном полноценный редактируемый пикер (его полем
          можно было стереть адрес прямо в корзине), на десктопе — инпут,
          в который нельзя печатать, но который открывает модалку по клику. */}
      <div className="mb-4">
        <AddressField isDark={isDark} />
      </div>

      {/* House Type Toggle */}
      <div className="mb-4">
        <div className="flex gap-2">
          <motion.button
            onClick={() => setDeliveryDetails({ isPrivateHouse: false })}
            className={cn(
              'flex-1 p-3 rounded-card border flex items-center justify-center gap-2 text-sm font-medium transition-all',
              !deliveryDetails.isPrivateHouse
                ? isDark
                  ? 'border-[var(--primary)] bg-[var(--primary-subtle)] text-[var(--primary)]'
                  : 'border-[var(--primary)] bg-[var(--violet-50)] text-[var(--primary)]'
                : isDark
                  ? 'border-[var(--border-default)] bg-[var(--surface-secondary)] text-[var(--text-secondary)]'
                  : 'border-[var(--border-default)] bg-[var(--surface-primary)] text-[var(--text-secondary)]'
            )}
            whileTap={{ scale: 0.98 }}
          >
            <BuildingsIcon weight={!deliveryDetails.isPrivateHouse ? 'fill' : 'regular'} className="w-5 h-5" />
            Квартира
          </motion.button>

          <motion.button
            onClick={() => setDeliveryDetails({ isPrivateHouse: true })}
            className={cn(
              'flex-1 p-3 rounded-card border flex items-center justify-center gap-2 text-sm font-medium transition-all',
              deliveryDetails.isPrivateHouse
                ? isDark
                  ? 'border-[var(--primary)] bg-[var(--primary-subtle)] text-[var(--primary)]'
                  : 'border-[var(--primary)] bg-[var(--violet-50)] text-[var(--primary)]'
                : isDark
                  ? 'border-[var(--border-default)] bg-[var(--surface-secondary)] text-[var(--text-secondary)]'
                  : 'border-[var(--border-default)] bg-[var(--surface-primary)] text-[var(--text-secondary)]'
            )}
            whileTap={{ scale: 0.98 }}
          >
            <HomeIcon weight={deliveryDetails.isPrivateHouse ? 'fill' : 'regular'} className="w-5 h-5" />
            Частный дом
          </motion.button>
        </div>
      </div>

      {/* Apartment Details (if not private house) */}
      {!deliveryDetails.isPrivateHouse && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Input
              id="apartment"
              label="Квартира *"
              value={deliveryDetails.apartment}
              onChange={(e) => setDeliveryDetails({ apartment: e.target.value })}
              error={errors.apartment ? 'Обязательное поле' : undefined}
            />

            <Input
              id="entrance"
              label="Подъезд *"
              value={deliveryDetails.entrance}
              onChange={(e) => setDeliveryDetails({ entrance: e.target.value })}
              error={errors.entrance ? 'Обязательное поле' : undefined}
            />

            <Input
              id="floor"
              label="Этаж *"
              value={deliveryDetails.floor}
              onChange={(e) => setDeliveryDetails({ floor: e.target.value })}
              error={errors.floor ? 'Обязательное поле' : undefined}
            />

            {/* Домофон обязателен только там, где он есть. В доме с открытым
                подъездом человеку нечего сюда написать — раньше он ставил
                прочерк или бросал заказ. */}
            <Input
              id="intercom"
              label={deliveryDetails.hasIntercom ? 'Домофон *' : 'Домофона нет'}
              value={deliveryDetails.intercom}
              onChange={(e) => setDeliveryDetails({ intercom: e.target.value })}
              error={errors.intercom ? 'Обязательное поле' : undefined}
              disabled={!deliveryDetails.hasIntercom}
            />
          </div>

          <button
            type="button"
            onClick={() =>
              setDeliveryDetails({
                hasIntercom: !deliveryDetails.hasIntercom,
                intercom: '',
              })
            }
            className={cn(
              'w-full mb-4 rounded-card border px-3 py-2.5 text-sm font-medium transition-colors',
              deliveryDetails.hasIntercom
                ? 'border-[var(--border-default)] bg-[var(--surface-secondary)] text-[var(--text-secondary)] hover:border-[var(--primary)]/50'
                : 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
            )}
          >
            {deliveryDetails.hasIntercom ? 'У меня нет домофона' : 'Домофон всё-таки есть'}
          </button>
        </>
      )}

      {/* Comment */}
      <div className="mb-4">
        <label className={cn(
          'block text-sm font-medium mb-2',
          'text-[var(--text-primary)]'
        )}>
          Комментарий к заказу
        </label>
        <div className="relative">
          <ChatTextIcon weight="regular" className={cn(
            'absolute left-3 top-3 w-5 h-5',
            'text-[var(--text-tertiary)]'
          )} />
          <textarea
            value={deliveryDetails.comment}
            onChange={(e) => setDeliveryDetails({ comment: e.target.value })}
            placeholder="Особые пожелания к заказу..."
            rows={3}
            className={cn(
              'w-full px-4 py-3 pl-11 rounded-card border text-[16px] resize-none',
              'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/40 focus:border-[var(--primary)]',
              isDark
                ? 'bg-[var(--surface-secondary)] border-[var(--border-default)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]'
                : 'bg-[var(--surface-primary)] border-[var(--border-default)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]'
            )}
          />
        </div>
      </div>

      {/* Preferences */}
      <div className="space-y-3">
        <ToggleOption
          active={preferences.noGreens}
          icon={LeafIcon}
          title="Без микрозелени"
          description="Зелень уберут, если она посыпается сверху"
          onClick={() => setPreferences({ noGreens: !preferences.noGreens })}
          isDark={isDark}
        />

        <ToggleOption
          active={preferences.needCall}
          icon={PhoneCallIcon}
          title="Заказать звонок оператора"
          description="По умолчанию оператор не перезванивает"
          onClick={() => setPreferences({ needCall: !preferences.needCall })}
          isDark={isDark}
        />
      </div>
    </div>
  );
}
